import { Controller } from "@hotwired/stimulus"
import { debug } from "../debug"
import { marked } from "../initializers/marked"
import DOMPurify from "dompurify"

// Configure DOMPurify to allow YouTube iframes while maintaining security (same as desktop)
const purifyConfig = {
  ADD_TAGS: ['iframe', 'div', 'a'],
  ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'src', 'style', 'width', 'height', 'title', 'class', 'href', 'target', 'rel'],
  ALLOW_DATA_ATTR: false,
  // Allow ALL legitimate protocols - https, http, mailto, tel, whatsapp
  // This blocks javascript: and data: URIs which are the actual XSS vectors
  ALLOWED_URI_REGEXP: /^(?:(?:https?|ftp|mailto|tel|whatsapp):)|^(?:www\.)|^(?:\/\/)/
};

// Connects to data-controller="mobile-chat"
export default class extends Controller {
  static targets = []
  static values = {
    debug: { type: Boolean, default: false }
  }
  
  connect() {
    debug("%c Mobile chat controller connected!", "background: #ff6b6b; color: white; padding: 4px; border-radius: 4px;");
    debug("Element bound to MobileChatController:", this.element);
    
    // Create modal right away
    this.createModal()
    
    // Intercept chat actions on mobile
    this.interceptChatActions()
    
    // Initialize state
    this.modalVisible = false
    this.originalMessages = null
    this.mobileModal = null
    
    // Make functions globally available - critical for interop with inline chat controller
    window.mobileChatFunctions = {
      open: this.openMobileChat.bind(this),
      close: this.closeMobileChat.bind(this),
      send: this.sendMessage.bind(this),
      isMobile: this.isMobile.bind(this),
      handleMessageResponse: this.handleMessageResponse.bind(this)
    }
    
    debug("%c Mobile chat functions set on window object", "background: #4ecdc4; color: white; padding: 4px; border-radius: 4px;", window.mobileChatFunctions);
    
    // Show debug indicator if debug mode is enabled
    if (this.hasDebugValue && this.debugValue) {
      const indicator = document.getElementById('mobileDebugIndicator');
      const status = document.getElementById('controllerStatus');
      
      if (indicator && status) {
        indicator.style.display = 'block';
        status.textContent = 'Connected ✅';
        status.style.color = '#4ecdc4';
      }
    }
    
    // Periodically check for API responses to ensure first messages are handled
    this.responseCheckInterval = setInterval(() => this.checkForResponses(), 500);
  }
  
  disconnect() {
    // Clean up on disconnect
    if (this.responseCheckInterval) {
      clearInterval(this.responseCheckInterval);
    }
  }
  
  // Check if on mobile device
  isMobile() {
    return window.matchMedia('(max-width: 768px)').matches
  }
  
  // Look for responses that need handling
  checkForResponses() {
    if (!this.modalVisible) return;
    
    // Get loading indicators from inline chat
    const loadingIndicators = document.querySelectorAll('.chat-loading');
    if (loadingIndicators.length > 0) {
      debug('Found loading indicators - waiting for response');
    }
    
    // This is where we could handle responses more actively if needed
  }
  
  // Handle a message response from the API (can be called by inline chat)
  handleMessageResponse(data) {
    if (!this.modalVisible) return;
    
    debug('Handling message response in mobile chat:', data);
    
    // Add AI response to chat
    if (data && data.response) {
      this.addMessageToChat(data.response, false);
    }
  }
  
  createModal() {
    debug('Creating mobile chat modal')
    
    // Don't create duplicate modals
    if (document.getElementById('mobile-chat-modal')) return
    
    // Create modal container
    const modal = document.createElement('div')
    modal.id = 'mobile-chat-modal'
    modal.className = 'mobile-chat-modal'
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100vh;
      background-color: white;
      z-index: 2147483647;
      display: none;
      flex-direction: column;
    `
    
    // Check language
    const lang = document.documentElement.lang || 'hr'
    const isEnglish = lang === 'en'
    
    // Create header
    const header = document.createElement('div')
    header.className = 'modal-header'
    header.style.cssText = `
      padding: 16px;
      border-bottom: 1px solid #e1e1e1;
      display: flex;
      justify-content: space-between;
      align-items: center;
    `
    
    const title = document.createElement('h3')
    title.className = 'modal-title'
    title.textContent = isEnglish ? 'Chat' : 'Chat'
    title.style.margin = '0'
    
    const closeBtn = document.createElement('button')
    closeBtn.className = 'modal-close'
    closeBtn.innerHTML = '&times;'
    closeBtn.style.cssText = `
      background: none;
      border: none;
      font-size: 24px;
      cursor: pointer;
      padding: 0 8px;
    `
    closeBtn.addEventListener('click', () => this.closeMobileChat())
    
    header.appendChild(title)
    header.appendChild(closeBtn)
    
    // Create body
    const body = document.createElement('div')
    body.className = 'modal-body'
    body.style.cssText = `
      flex: 1;
      overflow-y: auto;
      padding: 16px;
    `
    
    const messages = document.createElement('div')
    messages.id = 'mobile-messages'
    messages.className = 'chat-messages'
    body.appendChild(messages)
    
    // Create footer with input
    const footer = document.createElement('div')
    footer.className = 'modal-footer'
    footer.style.cssText = `
      padding: 16px;
      border-top: 1px solid #e1e1e1;
      background-color: #f8f8f8;
    `
    
    const form = document.createElement('form')
    form.className = 'modal-form'
    form.style.cssText = `
      display: flex;
      gap: 8px;
    `
    form.addEventListener('submit', (e) => { 
      e.preventDefault()
      return false
    })
    
    const textarea = document.createElement('textarea')
    textarea.id = 'mobile-input'
    textarea.className = 'search-input chat-textarea'
    textarea.placeholder = isEnglish ? 'Type your message...' : 'Upišite poruku...'
    textarea.style.cssText = `
      flex: 1;
      padding: 10px;
      border-radius: 18px;
      border: 1px solid #ccc;
      resize: none;
      min-height: 40px;
      max-height: 120px;
    `
    textarea.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        this.sendMessage()
        return false
      }
      return true
    })
    
    const sendBtn = document.createElement('button')
    sendBtn.id = 'mobile-send-button'
    sendBtn.className = 'search-button'
    sendBtn.textContent = isEnglish ? 'Send' : 'Pošalji'
    sendBtn.style.cssText = `
      min-width: 60px;
      border-radius: 18px;
      padding: 8px 16px;
      background-color: #5a9cd8;
      color: white;
      border: none;
      cursor: pointer;
    `
    sendBtn.addEventListener('click', () => this.sendMessage())
    
    form.appendChild(textarea)
    form.appendChild(sendBtn)
    footer.appendChild(form)
    
    // Assemble modal
    modal.appendChild(header)
    modal.appendChild(body)
    modal.appendChild(footer)
    
    // Add to body
    document.body.appendChild(modal)
    this.mobileModal = modal
    
    debug('Mobile modal created and added to DOM')
  }
  
  // Open the mobile chat - this is called directly by the test button
  openMobileChat() {
    debug('%c Opening mobile chat', 'background: #4ecdc4; color: white; padding: 2px 4px;')
    
    if (this.modalVisible) return
    
    // Ensure modal exists
    if (!this.mobileModal) {
      this.createModal()
    }
    
    const modal = document.getElementById('mobile-chat-modal')
    if (!modal) {
      console.error('Mobile modal not found')
      return
    }
    
    // Get the original messages container
    const originalContainer = document.querySelector('[data-inline-chat-target="messages"]')
    if (!originalContainer) {
      console.error('Original messages container not found')
      return
    }
    
    // Store original for later sync
    this.originalMessages = originalContainer
    
    // Get the mobile messages container
    const mobileMessages = document.getElementById('mobile-messages')
    if (!mobileMessages) {
      console.error('Mobile messages container not found')
      return
    }
    
    // Copy messages to mobile container
    mobileMessages.innerHTML = originalContainer.innerHTML
    
    // Hide the inline chat window
    const chatWindow = document.querySelector('[data-inline-chat-target="chatWindow"]')
    if (chatWindow) {
      chatWindow.style.display = 'none'
    }
    
    // Show modal with max z-index
    modal.style.display = 'flex'
    modal.style.zIndex = '2147483647'
    this.modalVisible = true
    
    // Scroll to bottom
    mobileMessages.scrollTop = mobileMessages.scrollHeight
    
    // Focus on input and transfer any existing text
    setTimeout(() => {
      const inlineInput = document.querySelector('[data-inline-chat-target="input"]')
      const mobileInput = document.getElementById('mobile-input')
      
      if (mobileInput) {
        if (inlineInput && inlineInput.value) {
          mobileInput.value = inlineInput.value
          inlineInput.value = ''
        }
        mobileInput.focus()
      }
    }, 100)
  }
  
  // Close the mobile chat
  closeMobileChat() {
    debug('Closing mobile chat')
    
    if (!this.modalVisible) return
    
    const modal = document.getElementById('mobile-chat-modal')
    const mobileMessages = document.getElementById('mobile-messages')
    
    // Hide modal
    if (modal) {
      modal.style.display = 'none'
    }
    
    // Show inline chat
    const chatWindow = document.querySelector('[data-inline-chat-target="chatWindow"]')
    if (chatWindow) {
      chatWindow.style.display = 'block'
    }
    
    // Transfer messages back to inline chat to keep in sync
    if (this.originalMessages && mobileMessages) {
      this.originalMessages.innerHTML = mobileMessages.innerHTML
      this.originalMessages.scrollTop = this.originalMessages.scrollHeight
    }
    
    this.modalVisible = false
    
    // Focus on inline input after closing modal
    const inlineInput = document.querySelector('[data-inline-chat-target="input"]')
    if (inlineInput) {
      inlineInput.focus()
    }
  }
  
  // Send a message from the mobile chat
  sendMessage() {
    debug('Sending message from mobile chat modal')
    
    const mobileInput = document.getElementById('mobile-input')
    if (!mobileInput || !mobileInput.value.trim()) return
    
    const message = mobileInput.value.trim()
    mobileInput.value = ''
    
    // Add user message to mobile chat
    this.addMessageToChat(message, true)
    
    // Add loading indicator
    const loadingEl = this.addLoadingIndicator()
    
    // SECURITY: No longer get session_id from localStorage
    // Server manages all session tokens securely
    
    // Prepare SECURE API request
    const payload = {
      message: message
      // SECURITY: No session_id - server handles this
    }
    
    // Send SECURE API request
    debug('🔒 Sending secure mobile message to API:', message)
    
    fetch('/api/v1/secure_chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      },
      body: JSON.stringify(payload)
    })
    .then(async response => {
      const data = await response.json()
      
      // Remove loading indicator
      if (loadingEl && loadingEl.parentNode) {
        loadingEl.remove()
      }
      
      // Check if the response contains an error (even with 200 status)
      if (data.error) {
        // Display the bilingual error message from Flask API
        this.addMessageToChat(data.error, false)
        return;
      }
      
      // SECURITY: No longer save session_id to localStorage
      // Server manages all session tokens securely
      debug('🔒 Secure mobile API response received')
      
      // Add AI response to chat
      this.addMessageToChat(data.response || 'Sorry, no response received', false)
    })
    .catch(error => {
      console.error('Error with chat API:', error)
      
      // Remove loading indicator
      if (loadingEl && loadingEl.parentNode) {
        loadingEl.remove()
      }
      
      // Add error message for network errors
      this.addMessageToChat('Sorry, there was an error processing your request.', false)
    })
  }
  
  // Add a message to the chat
  addMessageToChat(text, isUser) {
    // Add to mobile container
    const mobileMessages = document.getElementById('mobile-messages')
    if (mobileMessages) {
      const msgEl = document.createElement('div')
      msgEl.className = isUser ? 'user-message' : 'ai-message'
      msgEl.style.marginBottom = '16px'
      msgEl.style.padding = '12px 16px'
      msgEl.style.borderRadius = '8px'
      msgEl.style.maxWidth = '80%'
      
      if (isUser) {
        msgEl.style.backgroundColor = '#D7E5EE'
        msgEl.style.marginLeft = 'auto'
        msgEl.style.textAlign = 'right'
        msgEl.textContent = text
      } else {
        msgEl.style.backgroundColor = '#f0f0f0'
        msgEl.style.marginRight = 'auto'
        msgEl.style.textAlign = 'left'
        
        // Apply markdown rendering if available
        try {
          // marked is already configured with custom tokenizer in initializers/marked.js

          // Parse markdown FIRST
          const htmlContent = marked.parse(text)

          // Then process YouTube links on the HTML output
          const processedHtml = this.processYouTubeLinks(htmlContent)

          // Sanitize the final HTML for security
          msgEl.innerHTML = DOMPurify.sanitize(processedHtml, purifyConfig)
        } catch (e) {
          console.error('Error rendering markdown:', e)
          msgEl.textContent = text
        }
      }
      
      mobileMessages.appendChild(msgEl)
      mobileMessages.scrollTop = mobileMessages.scrollHeight
    }
    
    // Also add to original chat if it exists and we're not currently visible
    // This ensures messages are synced back when the modal is closed
    if (this.originalMessages && !this.modalVisible) {
      const msgEl = document.createElement('div')
      msgEl.className = isUser ? 'user-message' : 'ai-message'
      
      if (isUser) {
        msgEl.textContent = text
      } else {
        try {
          // Parse markdown FIRST
          const htmlContent = marked.parse(text)

          // Then process YouTube links on the HTML output
          const processedHtml = this.processYouTubeLinks(htmlContent)

          // Sanitize the final HTML for security
          msgEl.innerHTML = DOMPurify.sanitize(processedHtml, purifyConfig)
        } catch (e) {
          msgEl.textContent = text
        }
      }
      
      this.originalMessages.appendChild(msgEl)
      this.originalMessages.scrollTop = this.originalMessages.scrollHeight
    }
  }
  
  // Process YouTube links to embed videos (works on HTML content after markdown parsing)
  processYouTubeLinks(htmlContent) {
    // Handle YouTube links in href attributes (from markdown [text](url) which becomes <a href="url">text</a>)
    const linkRegex = /<a[^>]*href="((?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})[^"]*)"[^>]*>.*?<\/a>/g;

    htmlContent = htmlContent.replace(linkRegex, (match, url, videoId) => {
      return `<div class="youtube-embed">
        <iframe
          src="https://www.youtube.com/embed/${videoId}"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen>
        </iframe>
      </div>`;
    });

    // Also handle plain YouTube URLs in text (that aren't already in links)
    const plainUrlRegex = /(?<!href="|src=")(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:[^\s<]*)?/g;

    return htmlContent.replace(plainUrlRegex, (match, videoId) => {
      return `<div class="youtube-embed">
        <iframe
          src="https://www.youtube.com/embed/${videoId}"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowfullscreen>
        </iframe>
      </div>`;
    });
  }
  
  // Add a loading indicator to the chat
  addLoadingIndicator() {
    const container = document.getElementById('mobile-messages')
    if (!container) return null
    
    const indicator = document.createElement('div')
    indicator.className = 'loading-indicator'
    indicator.style.cssText = `
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 16px;
      padding: 12px 16px;
      background-color: #f0f0f0;
      border-radius: 8px;
      font-style: italic;
      color: #666;
      max-width: 80%;
      margin-right: auto;
    `
    
    const spinner = document.createElement('div')
    spinner.style.cssText = `
      width: 16px;
      height: 16px;
      border: 2px solid #ccc;
      border-top-color: #666;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    `
    
    // @keyframes spin lives in _inline_chat.scss (a script-injected <style> has no nonce and is blocked by the CSP)
    
    indicator.appendChild(document.createTextNode('Fiumed AI'))
    indicator.appendChild(spinner)
    
    container.appendChild(indicator)
    container.scrollTop = container.scrollHeight
    
    return indicator
  }
  
  // Intercept chat actions (like send button clicks) on mobile devices
  interceptChatActions() {
    debug('Setting up chat action interception')
    
    // Modify the inline chat controller's send method to delegate to mobile on mobile devices
    // This approach doesn't require direct DOM event listeners
    this.patchInlineChatController()
  }
  
  // Patch the inline chat controller to work with mobile
  patchInlineChatController() {
    // Wait for the inline chat controller to be available
    setTimeout(() => {
      // Find the inline chat controller
      const inlineControllers = window.Stimulus?.application?._controllers || []
      const inlineChatController = inlineControllers.find(c => c.identifier === 'inline-chat')
      
      if (inlineChatController) {
        debug('Found inline chat controller, patching send method')
        
        // Keep a reference to the original method
        const originalSend = inlineChatController.send
        
        // Override with our patched version
        inlineChatController.send = (event) => {
          // First get the message
          const message = inlineChatController.inputTarget.value.trim()
          
          // Check if we should use mobile
          if (message && this.isMobile()) {
            debug('Intercepted send in inline chat, delegating to mobile')
            
            // Let original method handle the message sending first
            originalSend.call(inlineChatController, event)
            
            // Then open the mobile modal after a small delay
            setTimeout(() => {
              this.openMobileChat()
            }, 100)
          } else {
            // Just use the original method on desktop
            originalSend.call(inlineChatController, event)
          }
        }
        
        // Also patch the original message handler to notify us of responses
        const originalFetchThen = inlineChatController.send.toString().match(/\.then\s*\(\s*data\s*=>\s*\{/);
        if (originalFetchThen) {
          debug('Successfully patched send method')
        } else {
          debugWarn('Could not patch send method - inline chat controller structure might have changed')
        }
      } else {
        debugWarn('Could not find inline chat controller to patch')
      }
    }, 500)
  }
}
